import { useEffect, useRef } from 'react';
import { editorStore } from '../../stores/editorStore';

const TRACKS = [
  { key: 'lighting', label: '光照', color: '#ffd76e' },
  { key: 'particles', label: '粒子', color: '#7ad0ff' },
  { key: 'audio', label: '音频', color: '#8aff8a' },
  { key: 'interactions', label: '交互', color: '#ff8a8a' },
];

/**
 * 底部时间轴：播放控制 + 轨道编排 + 播放头
 * 元素关键帧映射到“元素轨道”，场景事件映射到 4 条系统轨道
 */
export function TimelinePanel() {
  const page = editorStore((s) =>
    s.book?.pages.find((p) => p.id === s.currentPageId) ?? null
  );
  const playing = editorStore((s) => s.playing);
  const playhead = editorStore((s) => s.playhead);
  const setPlaying = editorStore((s) => s.setPlaying);
  const setPlayhead = editorStore((s) => s.setPlayhead);

  const rafRef = useRef<number | null>(null);

  const duration = page?.duration ?? 8;

  // 播放循环（本地累计时间，避免依赖 playhead 闭包抖动）
  useEffect(() => {
    if (!playing) return;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = (now - last) / 1000;
      last = now;
      const next = playhead + dt;
      if (next >= duration) {
        setPlayhead(0);
        setPlaying(false);
        return;
      }
      setPlayhead(next);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, duration, setPlayhead, setPlaying]);

  const seek = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPlayhead(parseFloat(e.target.value));
  };

  return (
    <div className="editor-timeline editor-panel">
      <div className="editor-timeline-top">
        <button
          className="editor-play-btn"
          onClick={() => setPlaying(!playing)}
          title={playing ? '暂停' : '播放'}
        >
          {playing ? '❚❚' : '▶'}
        </button>
        <span className="editor-time-label">
          {playhead.toFixed(1)}s / {duration.toFixed(1)}s
        </span>
        <input
          className="editor-timeline-slider"
          type="range"
          min={0}
          max={duration}
          step={0.01}
          value={playhead}
          onChange={seek}
        />
        <button
          className="editor-icon-btn"
          title="回到开头"
          onClick={() => {
            setPlayhead(0);
            setPlaying(false);
          }}
        >
          ↺
        </button>
      </div>

      <div className="editor-track-list">
        {/* 系统轨道 */}
        {TRACKS.map((track) => (
          <div key={track.key} className="editor-track">
            <span
              className="editor-track-label"
              style={{ borderLeft: `3px solid ${track.color}` }}
            >
              {track.label}
            </span>
            <div className="editor-track-body">
              {track.key === 'lighting' && (
                <div
                  className="editor-track-event"
                  style={{ left: '2%', width: '96%', background: `${track.color}33`, borderColor: track.color }}
                  title={`光照预设：${page?.lighting.preset ?? '-'}`}
                >
                  {page?.lighting.preset ?? '-'}
                </div>
              )}
              {track.key === 'particles' && page?.particles.enabled && (
                <div
                  className="editor-track-event"
                  style={{ left: '2%', width: '96%', background: `${track.color}33`, borderColor: track.color }}
                  title={`粒子预设：${page.particles.preset}`}
                >
                  {page.particles.preset}（{page.particles.count}）
                </div>
              )}
              {track.key === 'audio' && (page?.audio?.url || page?.audio?.narration) && (
                <div
                  className="editor-track-event"
                  style={{ left: '2%', width: '96%', background: `${track.color}33`, borderColor: track.color }}
                  title={page?.audio?.narration ?? '旁白'}
                >
                  旁白朗读
                </div>
              )}
            </div>
          </div>
        ))}

        {/* 元素轨道：每元素一行，关键帧以时间轴胶囊展示 */}
        {page?.elements.map((el) => (
          <div key={el.id} className="editor-track">
            <span className="editor-track-label" title={el.name}>
              {el.name}
            </span>
            <div className="editor-track-body">
              {(el.keyframes ?? []).map((kf) => (
                <div
                  key={kf.id}
                  className="editor-keyframe-pill"
                  style={{
                    left: `${(kf.time / duration) * 100}%`,
                  }}
                  title={`t=${kf.time}s easing=${kf.easing}`}
                />
              ))}
              {el.keyframes!.length === 0 && (
                <span className="editor-track-empty">无关键帧 · 在画布移动元素后添加</span>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="editor-timeline-hint">
        <span>播放头</span>
        <span>·</span>
        <span>关键帧胶囊</span>
        <span>·</span>
        <span>单击播放/暂停</span>
        <span>·</span>
        <span>Ctrl+Z 撤销</span>
      </div>
    </div>
  );
}